import { useEffect, useRef, useState } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import { automationService, type AutomationRule, type AutomationRuleLog, type RunSummary } from '../services/automation'
import api from '../../../lib/api'
import { LEAD_STATUS_LABELS } from '../../../lib/constants'
import {
  MessageSquare, Play, Plus, Pencil, Trash2, ChevronDown,
  CheckCircle, XCircle, AlertCircle, SkipForward, RefreshCw, X, ToggleLeft, ToggleRight, Calendar, List,
} from 'lucide-react'

// ── Helpers ───────────────────────────────────────────────────────────────────

const STATUS_OPTIONS = Object.entries(LEAD_STATUS_LABELS).map(([value, label]) => ({ value, label }))

const fmtDateTime = (s: string | null) =>
  s ? new Date(s).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'

const LOG_STATUS_BADGE: Record<string, string> = {
  SENT:    'badge-success',
  FAILED:  'badge-danger',
  SKIPPED: 'badge-neutral',
}

const LOG_STATUS_ICON = {
  SENT:    <CheckCircle className="h-3.5 w-3.5" />,
  FAILED:  <XCircle className="h-3.5 w-3.5" />,
  SKIPPED: <SkipForward className="h-3.5 w-3.5" />,
}

// ── DaysBeforeInput ──────────────────────────────────────────────────────────

function DaysBeforeInput({ value, onChange }: { value: number[]; onChange: (v: number[]) => void }) {
  const [inputVal, setInputVal] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  const add = () => {
    const n = parseInt(inputVal.trim(), 10)
    if (!isNaN(n) && n >= 0 && n <= 365 && !value.includes(n)) {
      onChange([...value, n].sort((a, b) => b - a))
    }
    setInputVal('')
    inputRef.current?.focus()
  }

  const remove = (n: number) => onChange(value.filter(v => v !== n))

  return (
    <div>
      <div className="flex flex-wrap gap-1.5 mb-2 min-h-[28px]">
        {value.map(n => (
          <span key={n} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
            style={{ background: 'var(--brand-primary-subtle)', color: 'var(--brand-primary)' }}>
            {n === 0 ? 'On due date' : `${n} days before`}
            <button type="button" onClick={() => remove(n)} className="opacity-60 hover:opacity-100">
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        {value.length === 0 && (
          <span className="text-xs" style={{ color: 'var(--text-disabled)' }}>No intervals set</span>
        )}
      </div>
      <div className="flex gap-2">
        <input
          ref={inputRef}
          type="number"
          className="input"
          style={{ width: 120 }}
          min={0}
          max={365}
          value={inputVal}
          onChange={e => setInputVal(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add() } }}
          placeholder="e.g. 30"
        />
        <button type="button" className="btn-secondary" onClick={add} style={{ height: 36 }}>
          Add
        </button>
      </div>
      <p className="form-hint">Enter days before renewal date and press Add or Enter. Messages are skipped if the customer replied to the previous one.</p>
    </div>
  )
}

// ── Rule form ─────────────────────────────────────────────────────────────────

interface RuleFormProps {
  initial?: Partial<AutomationRule>
  templates: string[]
  onSave: (data: Partial<AutomationRule>) => Promise<void>
  onCancel: () => void
}

function RuleForm({ initial, templates, onSave, onCancel }: RuleFormProps) {
  const [form, setForm] = useState({
    name:           initial?.name           ?? '',
    description:    initial?.description    ?? '',
    rule_type:      (initial?.rule_type     ?? 'status_delay') as 'status_delay' | 'renewal_schedule',
    trigger_status: initial?.trigger_status ?? '',
    delay_hours:    initial?.delay_hours    ?? 0,
    days_before:    initial?.days_before    ?? [] as number[],
    template_name:  initial?.template_name  ?? '',
    is_active:      initial?.is_active      ?? true,
  })
  const [saving, setSaving] = useState(false)
  const [error, setError]   = useState('')

  const set = <K extends keyof typeof form>(k: K, v: typeof form[K]) =>
    setForm(f => ({ ...f, [k]: v }))

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (form.rule_type === 'status_delay' && !form.trigger_status) {
      setError('Select a trigger status'); return
    }
    if (form.rule_type === 'renewal_schedule' && form.days_before.length === 0) {
      setError('Add at least one interval (days before renewal)'); return
    }
    if (!form.template_name) { setError('Select a template'); return }
    setSaving(true)
    setError('')
    try { await onSave(form) }
    catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setError(detail || 'Failed to save rule')
    } finally { setSaving(false) }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="alert-danger">
          <AlertCircle className="h-4 w-4 shrink-0" /><span>{error}</span>
        </div>
      )}

      {/* Rule type toggle */}
      <div className="form-group">
        <label className="form-label">Rule Type <span style={{ color: 'var(--danger-text)' }}>*</span></label>
        <div className="flex gap-2">
          <button
            type="button"
            className={form.rule_type === 'status_delay' ? 'btn-primary' : 'btn-secondary'}
            style={{ fontSize: 13, height: 34, padding: '0 14px' }}
            onClick={() => set('rule_type', 'status_delay')}
          >
            Status / Timing
          </button>
          <button
            type="button"
            className={form.rule_type === 'renewal_schedule' ? 'btn-primary' : 'btn-secondary'}
            style={{ fontSize: 13, height: 34, padding: '0 14px' }}
            onClick={() => set('rule_type', 'renewal_schedule')}
          >
            <Calendar className="h-3.5 w-3.5 mr-1.5" />
            Renewal Schedule
          </button>
        </div>
        <p className="form-hint">
          {form.rule_type === 'status_delay'
            ? 'Fires when a lead has been in a given status for the specified time'
            : 'Fires N days before the customer\'s insurance renewal date; skips if customer already replied'}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="form-group md:col-span-2">
          <label className="form-label">Rule Name <span style={{ color: 'var(--danger-text)' }}>*</span></label>
          <input type="text" className="input" required value={form.name}
            onChange={e => set('name', e.target.value)} placeholder="e.g. Renewal reminder sequence" />
        </div>

        {form.rule_type === 'status_delay' ? (
          <>
            <div className="form-group">
              <label className="form-label">Trigger Status <span style={{ color: 'var(--danger-text)' }}>*</span></label>
              <select className="input" value={form.trigger_status} onChange={e => set('trigger_status', e.target.value)}>
                <option value="">— select status —</option>
                {STATUS_OPTIONS.map(s => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
              <p className="form-hint">Lead must be in this status to qualify</p>
            </div>

            <div className="form-group">
              <label className="form-label">Delay (hours) <span style={{ color: 'var(--danger-text)' }}>*</span></label>
              <input type="number" className="input" min={0} max={720} step={0.5}
                value={form.delay_hours ?? 0}
                onChange={e => set('delay_hours', parseFloat(e.target.value) || 0)} />
              <p className="form-hint">0 = fire immediately when status matches</p>
            </div>
          </>
        ) : (
          <div className="form-group md:col-span-2">
            <label className="form-label">Send intervals <span style={{ color: 'var(--danger-text)' }}>*</span></label>
            <DaysBeforeInput
              value={form.days_before}
              onChange={v => set('days_before', v)}
            />
          </div>
        )}

        <div className="form-group md:col-span-2">
          <label className="form-label">WhatsApp Template <span style={{ color: 'var(--danger-text)' }}>*</span></label>
          <select className="input" value={form.template_name} onChange={e => set('template_name', e.target.value)}>
            <option value="">— select template —</option>
            {templates.map(t => (
              <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>
            ))}
          </select>
          <p className="form-hint">Variables will be auto-filled from lead data</p>
        </div>

        <div className="form-group md:col-span-2">
          <label className="form-label">Description</label>
          <textarea className="input" rows={2} value={form.description ?? ''}
            onChange={e => set('description', e.target.value)}
            placeholder="Optional — explain when this rule fires" />
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-2">
        <button type="button" className="btn-secondary" onClick={onCancel} disabled={saving}>Cancel</button>
        <button type="submit" className="btn-primary" disabled={saving}>
          {saving ? 'Saving…' : initial?.id ? 'Update Rule' : 'Create Rule'}
        </button>
      </div>
    </form>
  )
}

// ── Log drawer ────────────────────────────────────────────────────────────────

function LogDrawer({ rule, onClose }: { rule: AutomationRule; onClose: () => void }) {
  const [logs, setLogs]       = useState<AutomationRuleLog[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter]   = useState<string>('')

  useEffect(() => {
    automationService.getRuleLogs(rule.id)
      .then(setLogs)
      .finally(() => setLoading(false))
  }, [rule.id])

  const displayed = filter ? logs.filter(l => l.status === filter) : logs

  return (
    <div className="fixed inset-0 z-50 flex justify-end" style={{ background: 'rgba(0,0,0,0.4)' }} onClick={onClose}>
      <div className="h-full w-full max-w-xl flex flex-col overflow-hidden"
        style={{ background: 'var(--surface-card)', boxShadow: 'var(--shadow-xl)' }}
        onClick={e => e.stopPropagation()}>

        <div className="flex items-center justify-between px-5 py-4"
          style={{ borderBottom: '1px solid var(--border-default)' }}>
          <div>
            <h2 className="font-semibold" style={{ color: 'var(--text-primary)' }}>{rule.name}</h2>
            <p className="text-xs mt-0.5" style={{ color: 'var(--text-tertiary)' }}>Execution history</p>
          </div>
          <button className="btn-icon" onClick={onClose}><X className="h-4 w-4" /></button>
        </div>

        <div className="flex gap-1 px-5 pt-3" style={{ borderBottom: '1px solid var(--border-default)' }}>
          {['', 'SENT', 'FAILED', 'SKIPPED'].map(s => (
            <button key={s}
              onClick={() => setFilter(s)}
              className="px-3 py-1.5 text-xs font-medium border-b-2 -mb-px transition-colors"
              style={{
                borderBottomColor: filter === s ? 'var(--brand-primary)' : 'transparent',
                color: filter === s ? 'var(--brand-primary)' : 'var(--text-tertiary)',
              }}>
              {s || 'All'} {s && `(${logs.filter(l => l.status === s).length})`}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-2">
          {loading ? (
            <div className="space-y-2">
              {[1,2,3].map(i => <div key={i} className="skeleton h-14 rounded" />)}
            </div>
          ) : displayed.length === 0 ? (
            <p className="text-sm text-center py-8" style={{ color: 'var(--text-tertiary)' }}>No logs yet</p>
          ) : (
            displayed.map(log => (
              <div key={log.id} className="rounded-lg p-3 space-y-1"
                style={{ border: '1px solid var(--border-default)', background: 'var(--surface-subtle)' }}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className={`${LOG_STATUS_BADGE[log.status] ?? 'badge-neutral'} flex items-center gap-1`}>
                      {LOG_STATUS_ICON[log.status as keyof typeof LOG_STATUS_ICON]}
                      {log.status}
                    </span>
                    <span className="text-xs font-mono" style={{ color: 'var(--text-tertiary)' }}>
                      Lead #{log.lead_id}
                    </span>
                    {log.days_before_offset != null && (
                      <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                        ({log.days_before_offset === 0 ? 'due date' : `${log.days_before_offset}d before`})
                      </span>
                    )}
                  </div>
                  <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                    {fmtDateTime(log.executed_at)}
                  </span>
                </div>
                {log.error_message && (
                  <p className="text-xs font-mono px-2 py-1 rounded"
                    style={{ background: 'var(--danger-bg)', color: 'var(--danger-text)' }}>
                    {log.error_message}
                  </p>
                )}
                {log.skip_reason && (
                  <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                    Skipped: {log.skip_reason.replace(/_/g, ' ')}
                  </p>
                )}
                {log.variables_used && Object.keys(log.variables_used).length > 0 && (
                  <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                    Vars: {Object.entries(log.variables_used).map(([k,v]) => `${k}="${v}"`).join(', ')}
                  </p>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  )
}

// ── Trigger cell helper ───────────────────────────────────────────────────────

function TriggerCell({ rule }: { rule: AutomationRule }) {
  if (rule.rule_type === 'renewal_schedule') {
    const sorted = [...(rule.days_before ?? [])].sort((a, b) => b - a)
    return (
      <div className="flex flex-wrap gap-1">
        {sorted.map(n => (
          <span key={n} className="badge-info text-xs">
            {n === 0 ? 'Due date' : `${n}d before`}
          </span>
        ))}
      </div>
    )
  }
  return (
    <div>
      <span className="badge-info">
        {LEAD_STATUS_LABELS[rule.trigger_status ?? ''] ?? rule.trigger_status}
      </span>
      {!!rule.delay_hours && (
        <p className="text-xs mt-0.5" style={{ color: 'var(--text-tertiary)' }}>
          after {rule.delay_hours}h
        </p>
      )}
    </div>
  )
}

// ── Automation Logs tab ───────────────────────────────────────────────────────

const STATUS_FILTER_OPTIONS = ['ALL', 'SENT', 'FAILED', 'SKIPPED'] as const

function AllLogsTab() {
  const [logs, setLogs]       = useState<AutomationRuleLog[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter]   = useState<string>('ALL')

  const load = async (status?: string) => {
    setLoading(true)
    try {
      const data = await automationService.getAllLogs(status === 'ALL' ? undefined : status)
      setLogs(data)
    } finally { setLoading(false) }
  }

  useEffect(() => { load(filter) }, [filter])

  return (
    <div className="table-card">
      <div className="table-card-header">
        <h2 className="font-semibold" style={{ color: 'var(--text-primary)' }}>Automation Logs</h2>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1">
            {STATUS_FILTER_OPTIONS.map(s => (
              <button
                key={s}
                onClick={() => setFilter(s)}
                className={filter === s ? 'btn-primary' : 'btn-secondary'}
                style={{ fontSize: 12, height: 28, padding: '0 10px' }}
              >
                {s === 'ALL' ? 'All' : s.charAt(0) + s.slice(1).toLowerCase()}
              </button>
            ))}
          </div>
          <button onClick={() => load(filter)} disabled={loading} className="btn-icon" title="Refresh">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="data-table">
          <thead>
            <tr>
              <th>Lead</th>
              <th>Template</th>
              <th>Status</th>
              <th>Offset</th>
              <th>Sent At</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="py-10 text-center"><div className="spinner mx-auto" /></td></tr>
            ) : logs.length === 0 ? (
              <tr>
                <td colSpan={6} className="py-10 text-center" style={{ color: 'var(--text-tertiary)' }}>
                  No logs found.
                </td>
              </tr>
            ) : (
              logs.map(log => (
                <tr key={log.id}>
                  <td>
                    <p className="text-primary-data">{log.customer_name ?? `Lead #${log.lead_id}`}</p>
                    {log.vehicle_reg_no && (
                      <p className="text-xs mt-0.5 font-mono" style={{ color: 'var(--text-secondary)' }}>
                        {log.vehicle_reg_no}
                      </p>
                    )}
                    {log.insurance_due_date && (
                      <p className="text-xs mt-0.5" style={{ color: 'var(--text-tertiary)' }}>
                        Due {new Date(log.insurance_due_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </p>
                    )}
                  </td>
                  <td>
                    <span className="font-mono text-xs px-1.5 py-0.5 rounded"
                      style={{ background: 'var(--surface-subtle)', color: 'var(--text-secondary)' }}>
                      {log.template_name}
                    </span>
                  </td>
                  <td>
                    <span className={`badge ${LOG_STATUS_BADGE[log.status] ?? 'badge-neutral'} flex items-center gap-1 w-fit`}>
                      {LOG_STATUS_ICON[log.status as keyof typeof LOG_STATUS_ICON]}
                      {log.status}
                    </span>
                    {log.error_message && (
                      <p className="text-xs mt-0.5 font-mono" style={{ color: 'var(--danger-text)' }}>
                        {log.error_message}
                      </p>
                    )}
                    {log.skip_reason && (
                      <p className="text-xs mt-0.5" style={{ color: 'var(--text-tertiary)' }}>
                        {log.skip_reason.replace(/_/g, ' ')}
                      </p>
                    )}
                  </td>
                  <td>
                    {log.days_before_offset != null
                      ? <span className="text-xs">{log.days_before_offset === 0 ? 'Due date' : `${log.days_before_offset}d before`}</span>
                      : <span style={{ color: 'var(--text-disabled)' }}>—</span>
                    }
                  </td>
                  <td>
                    <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                      {fmtDateTime(log.executed_at ?? log.created_at)}
                    </span>
                  </td>
                  <td>
                    <Link
                      to={`/leads/${log.lead_id}`}
                      className="btn-secondary"
                      style={{ fontSize: 12, height: 28, padding: '0 10px', display: 'inline-flex', alignItems: 'center' }}
                    >
                      View Lead
                    </Link>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function AutomationPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const activeTab = searchParams.get('tab') === 'logs' ? 'logs' : 'rules'

  const [rules, setRules]         = useState<AutomationRule[]>([])
  const [templates, setTemplates] = useState<string[]>([])
  const [loading, setLoading]     = useState(true)
  const [showForm, setShowForm]   = useState(false)
  const [editRule, setEditRule]   = useState<AutomationRule | null>(null)
  const [logRule, setLogRule]     = useState<AutomationRule | null>(null)
  const [running, setRunning]     = useState(false)
  const [runResult, setRunResult] = useState<RunSummary | null>(null)
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null)

  const switchTab = (tab: 'rules' | 'logs') => {
    setSearchParams(tab === 'rules' ? {} : { tab })
  }

  const load = async () => {
    setLoading(true)
    try {
      const [r, t] = await Promise.all([
        automationService.listRules(),
        api.get('/whatsapp/templates').then(res =>
          (res.data as Array<{ name: string; approval_status: string }>)
            .filter(t => t.approval_status === 'approved')
            .map(t => t.name)
        ).catch(() => []),
      ])
      setRules(r)
      setTemplates(t)
    } finally { setLoading(false) }
  }

  useEffect(() => { load() }, [])

  const handleCreate = async (data: Partial<AutomationRule>) => {
    const { description, trigger_status, delay_hours, days_before, created_by, id, tenant_id, created_at, updated_at, ...rest } = data as AutomationRule
    await automationService.createRule({
      ...rest,
      description: description ?? undefined,
      trigger_status: trigger_status ?? undefined,
      delay_hours: delay_hours ?? undefined,
      days_before: days_before ?? undefined,
    })
    setShowForm(false)
    await load()
  }

  const handleUpdate = async (data: Partial<AutomationRule>) => {
    if (!editRule) return
    const { description: desc, trigger_status, delay_hours, days_before, created_by, id, tenant_id, created_at, updated_at, ...rest } = data as AutomationRule
    await automationService.updateRule(editRule.id, {
      ...rest,
      description: desc ?? undefined,
      trigger_status: trigger_status ?? undefined,
      delay_hours: delay_hours ?? undefined,
      days_before: days_before ?? undefined,
    })
    setEditRule(null)
    await load()
  }

  const handleToggle = async (rule: AutomationRule) => {
    await automationService.updateRule(rule.id, { is_active: !rule.is_active })
    setRules(prev => prev.map(r => r.id === rule.id ? { ...r, is_active: !r.is_active } : r))
  }

  const handleDelete = async (id: number) => {
    await automationService.deleteRule(id)
    setDeleteConfirm(null)
    setRules(prev => prev.filter(r => r.id !== id))
  }

  const handleRunNow = async () => {
    setRunning(true)
    setRunResult(null)
    try {
      const result = await automationService.runNow()
      setRunResult(result)
    } catch { /* shown via error state */ }
    finally { setRunning(false) }
  }

  return (
    <div className="space-y-6">

      <div className="section-header">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <MessageSquare className="h-6 w-6" style={{ color: 'var(--brand-primary)' }} />
            WhatsApp Automation
          </h1>
          <p className="page-subtitle">Rules that automatically send approved templates based on lead status or renewal schedule</p>
        </div>
        <div className="flex items-center gap-2">
          {activeTab === 'rules' && (
            <>
              <button
                onClick={handleRunNow}
                disabled={running}
                className="btn-secondary flex items-center gap-1.5"
                title="Run all active rules now without waiting for the 15-minute scheduler"
              >
                <Play className={`h-3.5 w-3.5 ${running ? 'animate-pulse' : ''}`} />
                {running ? 'Running…' : 'Run Now'}
              </button>
              <button
                onClick={() => { setShowForm(true); setEditRule(null) }}
                className="btn-primary"
              >
                <Plus className="h-3.5 w-3.5" /> New Rule
              </button>
            </>
          )}
        </div>
      </div>

      {/* Tab switcher */}
      <div className="flex gap-1 border-b" style={{ borderColor: 'var(--border-default)' }}>
        <button
          onClick={() => switchTab('rules')}
          className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium transition-colors"
          style={{
            color: activeTab === 'rules' ? 'var(--brand-primary)' : 'var(--text-secondary)',
            borderBottom: activeTab === 'rules' ? '2px solid var(--brand-primary)' : '2px solid transparent',
            marginBottom: -1,
          }}
        >
          <MessageSquare className="h-3.5 w-3.5" /> Rules
        </button>
        <button
          onClick={() => switchTab('logs')}
          className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium transition-colors"
          style={{
            color: activeTab === 'logs' ? 'var(--brand-primary)' : 'var(--text-secondary)',
            borderBottom: activeTab === 'logs' ? '2px solid var(--brand-primary)' : '2px solid transparent',
            marginBottom: -1,
          }}
        >
          <List className="h-3.5 w-3.5" /> Automation Logs
        </button>
      </div>

      {activeTab === 'logs' && <AllLogsTab />}

      {activeTab === 'rules' && runResult && (
        <div className={runResult.failed > 0 ? 'alert-danger' : 'alert-success'}>
          {runResult.failed > 0
            ? <AlertCircle className="h-4 w-4 shrink-0" />
            : <CheckCircle className="h-4 w-4 shrink-0" />}
          <div className="flex-1">
            <p className="text-sm font-medium">
              Run complete — {runResult.rules_evaluated} rule(s) evaluated, {runResult.leads_matched} lead(s) matched
            </p>
            <p className="text-xs mt-0.5" style={{ color: 'var(--text-secondary)' }}>
              ✅ {runResult.sent} sent · ❌ {runResult.failed} failed · ⏭ {runResult.skipped} skipped
            </p>
          </div>
          <button onClick={() => setRunResult(null)} className="opacity-60 hover:opacity-100">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {activeTab === 'rules' && (showForm || editRule) && (
        <div className="card">
          <h2 className="text-sm font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
            {editRule ? 'Edit Rule' : 'New Automation Rule'}
          </h2>
          <RuleForm
            initial={editRule ?? undefined}
            templates={templates}
            onSave={editRule ? handleUpdate : handleCreate}
            onCancel={() => { setShowForm(false); setEditRule(null) }}
          />
        </div>
      )}

      {activeTab === 'rules' && <div className="table-card">
        <div className="table-card-header">
          <h2 className="font-semibold" style={{ color: 'var(--text-primary)' }}>
            Automation Rules
          </h2>
          <button onClick={load} disabled={loading} className="btn-icon" title="Refresh">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th>Rule</th>
                <th>Type</th>
                <th>Trigger / Schedule</th>
                <th>Template</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="py-10 text-center"><div className="spinner mx-auto" /></td></tr>
              ) : rules.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-10 text-center" style={{ color: 'var(--text-tertiary)' }}>
                    No rules yet.{' '}
                    <button className="font-medium" style={{ color: 'var(--brand-primary)' }}
                      onClick={() => setShowForm(true)}>
                      Create one →
                    </button>
                  </td>
                </tr>
              ) : (
                rules.map(rule => (
                  <tr key={rule.id}>
                    <td>
                      <p className="text-primary-data">{rule.name}</p>
                      {rule.description && (
                        <p className="text-xs mt-0.5" style={{ color: 'var(--text-tertiary)' }}>{rule.description}</p>
                      )}
                    </td>
                    <td>
                      {rule.rule_type === 'renewal_schedule'
                        ? <span className="badge-purple flex items-center gap-1 w-fit"><Calendar className="h-3 w-3" />Renewal</span>
                        : <span className="badge-neutral">Status</span>
                      }
                    </td>
                    <td><TriggerCell rule={rule} /></td>
                    <td>
                      <span className="font-mono text-xs px-2 py-0.5 rounded"
                        style={{ background: 'var(--surface-subtle)', color: 'var(--text-secondary)' }}>
                        {rule.template_name}
                      </span>
                    </td>
                    <td>
                      <button onClick={() => handleToggle(rule)}
                        className="flex items-center gap-1.5 text-xs font-medium transition-colors"
                        style={{ color: rule.is_active ? 'var(--success-text)' : 'var(--text-tertiary)' }}>
                        {rule.is_active
                          ? <ToggleRight className="h-4 w-4" />
                          : <ToggleLeft className="h-4 w-4" />}
                        {rule.is_active ? 'Active' : 'Paused'}
                      </button>
                    </td>
                    <td>
                      <div className="flex items-center gap-1">
                        <button title="View execution logs" className="btn-icon" onClick={() => setLogRule(rule)}>
                          <ChevronDown className="h-3.5 w-3.5" />
                        </button>
                        <button title="Edit rule" className="btn-icon"
                          onClick={() => { setEditRule(rule); setShowForm(false) }}>
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        {deleteConfirm === rule.id ? (
                          <div className="flex items-center gap-1">
                            <button className="text-xs font-medium" style={{ color: 'var(--danger-text)' }}
                              onClick={() => handleDelete(rule.id)}>Confirm</button>
                            <button className="text-xs" style={{ color: 'var(--text-tertiary)' }}
                              onClick={() => setDeleteConfirm(null)}>Cancel</button>
                          </div>
                        ) : (
                          <button title="Delete rule" className="btn-icon"
                            onClick={() => setDeleteConfirm(rule.id)}
                            style={{ color: 'var(--danger-text)' }}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>}

      {activeTab === 'rules' && logRule && <LogDrawer rule={logRule} onClose={() => setLogRule(null)} />}
    </div>
  )
}
